import { buildPivotSheet } from './reportDataExport'

function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function buildDateList(start, end) {
  const list = []
  const cursor = new Date(start)
  const endDate = new Date(end)
  while (cursor <= endDate) {
    list.push(formatDate(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return list
}

function defaultPreviewRange(config) {
  if (config?.exportDateRange?.[0] && config?.exportDateRange?.[1]) {
    return [config.exportDateRange[0], config.exportDateRange[1]]
  }
  const end = new Date()
  end.setDate(end.getDate() - 1)
  const start = new Date(end)
  start.setDate(start.getDate() - 2)
  return [formatDate(start), formatDate(end)]
}

function sortTrafficChannels(list = []) {
  return [...list].sort((a, b) => {
    if (a === '__overall__') return -1
    if (b === '__overall__') return 1
    return String(a).localeCompare(String(b), 'en')
  })
}

/**
 * 根据模板/导出配置生成「示意 Excel」sheet（数值为 0，仅展示表头与行列结构）
 */
export function buildPreviewSheetsFromConfig(config) {
  if (!config?.exportSheets?.length) return []
  const exportForm = config.exportForm || {}
  const [start, end] = defaultPreviewRange(config)
  const dates = buildDateList(start, end)
  const orientation = exportForm.orientation || 'date-row'
  const themeKey = exportForm.themeKey || 'ocean'

  return config.exportSheets.map((sheetConfig) => {
    if (sheetConfig.category === 'traffic') {
      return buildTrafficPreviewSheet(sheetConfig, dates, exportForm, orientation, themeKey)
    }
    return buildBehaviorPreviewSheet(sheetConfig, dates, exportForm, orientation, themeKey)
  })
}

function buildTrafficPreviewSheet(sheetConfig, dates, exportForm, orientation, themeKey) {
  const channelConfigs = sheetConfig.channelConfigs?.length
    ? sheetConfig.channelConfigs
    : (sheetConfig.channels || ['__overall__']).map((ch) => ({
      channel: ch,
      metrics: sheetConfig.metrics || ['pv', 'uv']
    }))

  const sortedConfigs = sortTrafficChannels(channelConfigs.map((c) => c.channel))
    .map((ch) => channelConfigs.find((c) => c.channel === ch))
    .filter(Boolean)

  const items = sortedConfigs.map((cfg) => (cfg.channel === '__overall__' ? '整体' : cfg.channel))
  const allMetricKeys = [...new Set(sortedConfigs.flatMap((c) => c.metrics || []))]
  const valueMap = {}

  sortedConfigs.forEach((cfg) => {
    const label = cfg.channel === '__overall__' ? '整体' : cfg.channel
    valueMap[label] = {}
    dates.forEach((d) => {
      valueMap[label][d] = {}
      ;(cfg.metrics || []).forEach((m) => { valueMap[label][d][m] = 0 })
    })
  })

  return buildPivotSheet({
    sheetName: sheetConfig.sheetName,
    orientation,
    dateList: dates,
    metrics: allMetricKeys.length ? allMetricKeys : ['pv', 'uv'],
    items: items.length ? items : ['整体'],
    valueMap,
    themeKey,
    includeSumRow: !!(exportForm.includeSummaryRow && !exportForm.summaryDedup && !exportForm.summaryCumDaily),
    includeDailySumRow: !!(exportForm.includeSummaryRow && exportForm.summaryCumDaily),
    includeDedupRow: !!(exportForm.includeSummaryRow && exportForm.summaryDedup),
    noCumDailyKeys: ['sumStayTime', 'avgStayTime'],
    itemMetrics: Object.fromEntries(
      sortedConfigs.map((cfg) => [
        cfg.channel === '__overall__' ? '整体' : cfg.channel,
        cfg.metrics || []
      ])
    )
  })
}

function buildBehaviorPreviewSheet(sheetConfig, dates, exportForm, orientation, themeKey) {
  const targets = sheetConfig.channels?.length ? sheetConfig.channels : ['__overall__']
  const behaviors = sheetConfig.behaviors || []
  const mergedGroups = (sheetConfig.mergedBehaviors || []).filter((m) => m.behaviors?.length)
  const items = []

  sortTrafficChannels(targets).forEach((target) => {
    const channelLabel = target === '__overall__' ? '整体' : target
    behaviors.forEach((behaviorName) => {
      items.push(targets.length > 1 ? `${channelLabel} / ${behaviorName}` : behaviorName)
    })
  })

  mergedGroups.forEach((merged) => {
    items.push(merged.name || (merged.behaviors || []).join('+'))
  })

  if (!items.length) items.push('（请在配置中添加行为）')

  const valueMap = {}
  items.forEach((item) => {
    valueMap[item] = {}
    dates.forEach((d) => { valueMap[item][d] = { pv: 0, uv: 0 } })
  })

  return buildPivotSheet({
    sheetName: sheetConfig.sheetName,
    orientation,
    dateList: dates,
    metrics: ['pv', 'uv'],
    items,
    valueMap,
    themeKey,
    includeSumRow: !!(exportForm.includeSummaryRow && !exportForm.summaryDedup && !exportForm.summaryCumDaily),
    includeDailySumRow: !!(exportForm.includeSummaryRow && exportForm.summaryCumDaily),
    includeDedupRow: !!(exportForm.includeSummaryRow && exportForm.summaryDedup),
    noCumDailyKeys: []
  })
}

/** 将 sheet.rows + merges 转为可渲染的表格行（含 colspan/rowspan） */
export function buildSheetRenderGrid(sheet) {
  const rows = sheet?.rows || []
  const merges = sheet?.merges || []
  const rowCount = rows.length
  const colCount = Math.max(...rows.map((r) => r.length), 0)
  const hidden = new Set()
  const spanMap = new Map()

  merges.forEach((m) => {
    spanMap.set(`${m.row},${m.col}`, { rowspan: m.down + 1, colspan: m.across + 1 })
    for (let r = m.row; r <= m.row + m.down; r += 1) {
      for (let c = m.col; c <= m.col + m.across; c += 1) {
        if (r !== m.row || c !== m.col) hidden.add(`${r},${c}`)
      }
    }
  })

  const grid = []
  for (let r = 0; r < rowCount; r += 1) {
    const tr = []
    for (let c = 0; c < colCount; c += 1) {
      if (hidden.has(`${r},${c}`)) continue
      const cell = rows[r]?.[c] || { value: '', role: 'body' }
      const span = spanMap.get(`${r},${c}`) || { rowspan: 1, colspan: 1 }
      tr.push({ cell, rowspan: span.rowspan, colspan: span.colspan })
    }
    grid.push(tr)
  }
  return grid
}

export function formatPreviewCellValue(cell) {
  const v = cell?.value
  if (v === null || v === undefined || v === '') return ''
  if (cell?.format === 'percent' && typeof v === 'number') {
    return `${(v * 100).toFixed(2)}%`
  }
  if (cell?.format === 'decimal' && typeof v === 'number') {
    return v.toFixed(2)
  }
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(2)
  return String(v)
}
